import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { portalIdentityKey } from "../auth/usePortalIdentity";

/**
 * The writes account settings makes against the portal's own gateways.
 *
 * Only the profile is here. The password and the second factor are GoTrue's, not this database's,
 * and they live in `app/auth/` with the rest of the session machinery rather than being dressed up
 * as portal queries.
 */

export interface ProfileNames {
  firstName: string;
  displayName: string;
}

/**
 * Writes the person's own name.
 *
 * `public.update_own_profile` coalesces every argument, so a null keeps what is stored. That is why
 * this sends trimmed strings and the form refuses an empty field rather than sending `''`:
 * `profiles_first_name_length_check` is `between 1 and 120`, so an empty string is rejected by the
 * database and a null silently changes nothing. Neither is what a person clearing a field means, and
 * the honest answer is that this gateway cannot remove a name. The form says so instead of appearing
 * to accept it.
 *
 * The identity query is invalidated rather than patched. The rail, the overview greeting and every
 * thread label read from it, and a hand-written cache update would have to agree with the server's
 * own coalescing rules to stay true.
 */
export function useUpdateOwnProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ firstName, displayName }: ProfileNames) => {
      const supabase = getSupabaseBrowserClient();
      const { error } = await supabase.rpc("update_own_profile", {
        p_first_name: firstName.trim(),
        p_display_name: displayName.trim()
      });

      if (error) {
        throw error;
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: portalIdentityKey })
  });
}
